import { InMemoryCache, ApolloLink, HttpLink } from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { RetryLink } from '@apollo/client/link/retry';
import { persistCache, LocalStorageWrapper } from 'apollo3-cache-persist';
// ApolloLinkTimeout is not an Apollo dependency: https://github.com/drcallaway/apollo-link-timeout
// No official replacement in Apollo Client v3.x
import ApolloLinkTimeout from 'apollo-link-timeout';
import {
  getAPIConfig,
  generateTraceId,
  getStorage,
  getHashKey,
  isAPITriggerLoggerEnabled,
  getPlaceSiteId,
  getSubSetAPiConfigKey,
} from '@tcp/core/src/utils';
import { getStoreRef } from '@tcp/core/src/utils/store.utils';
import { setBrandSwitchedApolloStatefulClient } from '@tcp/core/src/reduxStore/actions';
import { getBrandSwitchedStateApolloStatefulGraqhQl } from '@tcp/core/src/reduxStore/selectors/global.selectors';
import { USID_DUP, USID_EXP } from '@tcp/core/src/services/api.constants';
import ApiTriggerTracker from '@tcp/core/src/utils/apiTriggerLogger';
import logger from '@tcp/core/src/utils/loggerInstance';
import ApolloClientBase from './ApolloClientBase';
import {
  CACHE_TAG_HEADER,
  QUERY_TYPE_HEADER,
  STATEFUL_TRACE_ID_HEADER,
  STATEFUL_SESSION_ID_HEADER,
  CLIENT_SOURCE,
} from '../../../../constants/headers.constants';
import {
  getStatefulClientOptions,
  getFetchPolicy,
  getDataFromStorage,
  afterResponseLink,
  getIsPassUsidInHeaderEnabled,
} from './utils';
import RetryConfig from './retryConfiguration';
import { generateSessionId } from '../../../../utils/cookie.util';
import { getAmznTraceId, isServer } from '../../../../utils/utils';

let apolloClient = null;

/**
 * Class ApolloStatefulClient which extends ApolloClientBase
 */

const retryIf = (error) => {
  const retryErrorCodes = [500, 400, undefined];
  return !!error && retryErrorCodes.includes(error.statusCode);
};

const getRetryData = () => {
  return {
    attempts: (count, operation, error) => {
      const { maxAttempt = 0 } = RetryConfig[operation.operationName] || {};
      return retryIf(error) && operation.operationName in RetryConfig && count <= maxAttempt;
    },
    delay: (count, operation) => {
      const { maxDelay = 0 } = RetryConfig[operation.operationName];
      return operation.operationName in RetryConfig && maxDelay;
    },
  };
};

class ApolloStatefulClient extends ApolloClientBase {
  constructor(options) {
    super({ ...options });
  }

  /**
   * Generate the full cache tag string based on contextual information
   * such as Path Context, Brand Id etc.
   *
   * @param {string} cacheTagPrefix
   * @param {boolean} isContextual
   * @returns {string} Full cache tag string
   */
  generateCacheTag = (cacheTagPrefix, isContextual, apiConfig) => {
    let enhancedCacheTag;

    // "isContextual" argument is sent as true for "page level module calls" and in future -
    // other calls which are bound to page context
    if (isContextual) {
      const pathContext = this.getPathContext(apiConfig);
      enhancedCacheTag = `${cacheTagPrefix}-${apiConfig.brandId}-${pathContext}`;
    } else {
      enhancedCacheTag = `${cacheTagPrefix}-${apiConfig.brandId}-${apiConfig.siteId}`;
    }
    return enhancedCacheTag;
  };

  apiTriggerLogger = (query) => {
    try {
      if (isAPITriggerLoggerEnabled() && window && typeof window !== 'undefined') {
        const apiTriggerLoggerInstance = new ApiTriggerTracker();
        const trackerObject = apiTriggerLoggerInstance.getTrackerObject();
        if (trackerObject[query] && trackerObject[query].startListening) {
          apiTriggerLoggerInstance.setInvokedValue(query, true);
        }
      }
    } catch {
      console.error('error in apiTriggerLogger in interceptor');
    }
  };

  isValidUSID = (usid, usidExp) => {
    const currentTime = new Date().getTime();
    const storedExpirationTime = parseInt(usidExp, 10);
    return usid && usidExp && currentTime < storedExpirationTime;
  };

  addUsidDup = (headers, usid, usidExp) => {
    const modifiedHeaders = headers;
    if (this.isValidUSID(usid, usidExp)) {
      modifiedHeaders[USID_DUP] = usid;
    }
    return modifiedHeaders;
  };

  /**
   * This function executes graphQL query
   * @param {*} query GraphQL query
   * @returns {Promise} Resolves with data or rejects with error object
   */
  async executeQuery(
    query,
    apiConfig,
    { cacheTag, isContextual, variables, fetchPolicy, context, header, callbackParams },
    type = 'query',
    updateQuery,
    cacheConfigKey,
    TTL
  ) {
    const { isMobile, isAppChannel, uuidEnabled } = apiConfig;
    let deviceType = isMobile ? 'mobile' : 'desktop';
    let channel = '';
    if (isAppChannel) {
      deviceType = 'App';
      channel = 'app';
    }
    const queryName = query?.definitions[0]?.name?.value;
    let headers = { deviceType, 'x-tcp-channel': channel, siteId: getPlaceSiteId() };
    if (header) {
      headers = { ...headers, ...header };
    }
    if (cacheTag) {
      const enhancedCacheTag = this.generateCacheTag(cacheTag, isContextual, apiConfig);
      headers[CACHE_TAG_HEADER] = enhancedCacheTag;
      headers[QUERY_TYPE_HEADER] = `${cacheTag}-${apiConfig.brandId}-${apiConfig.siteId}`;
    }
    let traceId;
    if (uuidEnabled) {
      traceId = generateTraceId(uuidEnabled);
    } else {
      traceId = generateTraceId();
    }
    const amznTraceId = getAmznTraceId(traceId);
    if (amznTraceId) {
      traceId = `${traceId}_${amznTraceId}`;
    }

    headers[STATEFUL_TRACE_ID_HEADER] = traceId;

    const pathContext = this.getPathContext(apiConfig);
    headers[CLIENT_SOURCE] = `${apiConfig?.brandId}-${pathContext}`;

    const sessionId = generateSessionId(true) || apiConfig.reqSessionCookie || 'not-available';

    headers[STATEFUL_SESSION_ID_HEADER] = sessionId;
    const passUsidInHeaderEnabled = getIsPassUsidInHeaderEnabled();
    if (passUsidInHeaderEnabled) {
      const usid = await getDataFromStorage(USID_DUP);
      if (usid) {
        const usidExp = await getDataFromStorage(USID_EXP);
        headers = this.addUsidDup(headers, usid, usidExp);
      }
    }

    this.apiTriggerLogger(queryName);

    return this.executeQueryPromise(
      query,
      apiConfig,
      {
        headers,
        fetchPolicy: fetchPolicy || getFetchPolicy({ type, cacheConfigKey, TTL }),
        variables,
        handleError: true,
        newClient: true,
        context,
        callbackParams,
      },
      type,
      updateQuery,
      cacheConfigKey,
      TTL
    );
  }
}

const customFetch = (uri, options) => {
  const apiConfig = getAPIConfig();
  logger.info('[SSR]customFetch##API URL', uri, '##', getSubSetAPiConfigKey(apiConfig));
  if (options.method === 'POST') {
    const { operationName } = JSON.parse(options.body);
    return fetch(`${uri}?operationName=${operationName}`, options);
  }
  return fetch(uri, options);
};

const getApolloClientOptions = () => {
  return new Promise(async (resolve) => {
    const apiConfig = getAPIConfig();
    const options = getStatefulClientOptions(apiConfig);

    const httpLink = new HttpLink({ fetch: customFetch, uri: options.url });
    const retryLink = new RetryLink(getRetryData());
    const timeoutLink = new ApolloLinkTimeout(apiConfig.GRAPHQL_API_TIMEOUT_STATEFUL);
    const passUsidInHeaderEnabled = getIsPassUsidInHeaderEnabled();
    let link = null;
    if (apiConfig.graphql_apq_stateful_disabled === 'true') {
      link = passUsidInHeaderEnabled
        ? ApolloLink.from([timeoutLink, retryLink, afterResponseLink, httpLink])
        : ApolloLink.from([timeoutLink, retryLink, httpLink]);
    } else {
      link = passUsidInHeaderEnabled
        ? ApolloLink.from([
            createPersistedQueryLink({ sha256: getHashKey, useGETForHashedQueries: true }),
            timeoutLink,
            retryLink,
            afterResponseLink,
            httpLink,
          ])
        : ApolloLink.from([
            createPersistedQueryLink({ sha256: getHashKey, useGETForHashedQueries: true }),
            timeoutLink,
            retryLink,
            httpLink,
          ]);
    }
    logger.info(`Stateful GraphQL API base url ${options?.url}`);
    const cache = new InMemoryCache();
    if (!isServer()) {
      const storage = getStorage(); // Can't import AsyncStorage lib here so moved it to utils
      const storageWrapper = new LocalStorageWrapper(storage);
      await persistCache({
        cache,
        storage: storageWrapper,
      });
    }
    resolve({
      name: apiConfig.isAppChannel ? 'app' : 'web',
      version: '0',
      link,
      cache,
    });
  });
};

const ApolloClientInstance = () => {
  const storeRef = getStoreRef();
  let isBrandSwitched = false;
  const store = (storeRef && storeRef.getState()) || {};
  if (Object.keys(store).length) {
    isBrandSwitched = getBrandSwitchedStateApolloStatefulGraqhQl(store);
  }
  return new Promise(async (resolve) => {
    if (!apolloClient || isBrandSwitched || isServer()) {
      const options = await getApolloClientOptions();
      apolloClient = new ApolloStatefulClient(options);
      if (isBrandSwitched) {
        storeRef.dispatch(setBrandSwitchedApolloStatefulClient(false));
      }
    }

    resolve(apolloClient);
  });
};

export default ApolloClientInstance;
